import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search } from "lucide-react";
import { format } from "date-fns";
import { useMobile } from "@/hooks/useMobile";

interface AuditLog {
  id: string;
  action_type: string;
  resource_type: string;
  resource_id: string;
  action_details: any;
  created_at: string;
  admin_users?: {
    profiles?: {
      full_name: string;
      email: string;
    };
  };
}

export default function SystemAuditLog() {
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionTypeFilter, setActionTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [searchQuery, actionTypeFilter, logs]);

  async function loadLogs() {
    try {
      const { data, error } = await supabase
        .from('admin_audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;
      setLogs((data || []) as any);
    } catch (error: any) {
      console.error('Error loading audit logs:', error);
      toast({
        title: "Error",
        description: "Failed to load audit logs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  function filterLogs() {
    let filtered = logs;
    
    if (searchQuery) {
      filtered = filtered.filter(log =>
        log.action_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.resource_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.resource_id?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (actionTypeFilter !== "all") {
      filtered = filtered.filter(log => log.action_type.includes(actionTypeFilter));
    }
    
    setFilteredLogs(filtered);
  }

  function getActionTypeColor(actionType: string) {
    if (actionType.includes('create')) return 'bg-green-500';
    if (actionType.includes('update')) return 'bg-blue-500';
    if (actionType.includes('delete')) return 'bg-red-500';
    if (actionType.includes('login')) return 'bg-purple-500';
    return 'bg-gray-500';
  }

  // Mobile audit log card
  const MobileAuditCard = ({ log }: { log: AuditLog }) => (
    <Card className="mb-3">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <Badge className={getActionTypeColor(log.action_type)}>
            {log.action_type.replace('_', ' ')}
          </Badge>
          <span className="text-xs text-muted-foreground">{format(new Date(log.created_at), 'PP p')}</span>
        </div>
        <div className="space-y-1 text-sm">
          <div>
            <span className="text-muted-foreground">Admin: </span>
            <span>{log.admin_users?.profiles?.full_name || 'Unknown'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Resource: </span>
            <span>{log.resource_type}</span>
          </div>
          {log.resource_id && (
            <div className="text-xs font-mono text-muted-foreground truncate">
              ID: {log.resource_id}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">System Audit Log</h1>
        <p className="text-muted-foreground text-sm">Complete history of admin actions</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search audit logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 min-h-[44px]"
          />
        </div>
        <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
          <SelectTrigger className="w-full sm:w-[180px] min-h-[44px]">
            <SelectValue placeholder="Action Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="login">Login</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Card View */}
      {isMobile ? (
        <div>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No logs found</div>
          ) : (
            filteredLogs.map((log) => (
              <MobileAuditCard key={log.id} log={log} />
            ))
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Admin</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource Type</TableHead>
                <TableHead>Resource ID</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">No logs found</TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="text-sm">
                        <div>{log.admin_users?.profiles?.full_name || 'Unknown'}</div>
                        <div className="text-muted-foreground text-xs">
                          {log.admin_users?.profiles?.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getActionTypeColor(log.action_type)}>
                        {log.action_type.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{log.resource_type}</TableCell>
                    <TableCell className="font-mono text-xs">{log.resource_id || '-'}</TableCell>
                    <TableCell className="text-sm">{format(new Date(log.created_at), 'PPpp')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
